import { isEqual, omit } from 'lodash';

type LedgerRow = Record<string, unknown> & { shares: string; price: string };

const ledgerNumber = (value: unknown): string | undefined => {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return undefined;
  }
  const text = String(value).trim();
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(text)) {
    return undefined;
  }
  const number = Number(text);
  return Number.isFinite(number) && number >= 0 ? text : undefined;
};

/** Converts the external options ladder as one validated ledger, preserving canonical row identities. */
export const normalizeOptionsLedger = (value: unknown): LedgerRow[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const rows: LedgerRow[] = [];
  const valid = value.every(row => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) {
      return false;
    }
    const canonical = 'shares' in row || 'price' in row;
    if (canonical && ('count' in row || 'strikePrice' in row)) {
      return false;
    }
    const shares = ledgerNumber(canonical ? row.shares : row.count);
    const price = ledgerNumber(canonical ? row.price : row.strikePrice);
    if (shares === undefined || price === undefined) {
      return false;
    }
    const normalized = { ...row, shares, price };
    if (!canonical) {
      delete normalized.count;
      delete normalized.strikePrice;
    }
    rows.push(normalized);
    return true;
  });
  return valid ? rows : undefined;
};

// Compare decimal spellings without rounding the ledger's persisted precision.
const decimalKey = (value: string): string => {
  const [mantissa, exponent = '0'] = value.replace(/^[+-]/, '').toLowerCase().split('e');
  const [whole, fraction = ''] = mantissa.split('.');
  const digits = `${whole}${fraction}`.replace(/^0+/, '');
  if (!digits) return '0';
  const trailingZeros = digits.match(/0+$/)?.[0].length ?? 0;
  const power = Number(exponent) - fraction.length + trailingZeros;
  return Number.isSafeInteger(power) ? `${digits.slice(0, digits.length - trailingZeros)}e${power}` : value;
};

/** Only shares and price use numeric equality; row identities and extra data remain exact. */
export const sameOptionsLedger = (left: unknown, right: unknown): boolean => {
  const a = normalizeOptionsLedger(left);
  const b = normalizeOptionsLedger(right);
  return a !== undefined && b !== undefined && a.length === b.length && a.every((row, index) =>
    decimalKey(row.shares) === decimalKey(b[index].shares) &&
    decimalKey(row.price) === decimalKey(b[index].price) &&
    isEqual(omit(row, ['shares', 'price']), omit(b[index], ['shares', 'price']))
  );
};
